<template lang="pug">
  .d-inline-block
    v-dialog(v-model="showCreateModal" persistent scrollable lazy  width="50%")
      v-card(data-cy="create-contact-modal")
        title-bar(dark :loading="contactInProgress") Create Contact
        v-card-text(ref="newContactForm")
          v-layout.contact-info(row wrap)
            v-flex(xs12)
              h3 Personal Information
            v-flex(xs6)
              v-form(v-model="firstNameValid" ref="firstNameForm" lazy-validation)
                v-text-field(
                  label="First Name*"
                  browser-autocomplete="disable"
                  v-model="params.first_name"
                  required
                  prepend-icon="person"
                  :rules="[firstNameRules.required, firstNameRules.minLength(firstNameMinLegth), firstNameRules.onlyAllowedCharacters, firstNameRules.denySpecificWords(firstNameProhibitedWords)]"
                )
            v-flex(xs6)
              v-text-field(
                label="Last Name"
                browser-autocomplete="disable"
                v-model="params.last_name"
                required
                prepend-icon="person"
              )
            v-flex(xs12 v-if="showRelation")
              v-select(
                v-model="params[relation.name]"
                ref="relationField"
                item-disabled="disabled"
                :items="relation.items"
                :label="relation.label"
                :prepend-icon="relation.icon"
                :hint="relation.hint"
                :error='fieldValueErrorTypes.includes(relation.label)'
                :error-messages="fieldValueErrorTypes.includes(relation.label) ? [fieldValueErrorMessages[relation.label]] : []"
                persistent-hint
              )
            v-flex(xs12 v-for="(field, index) in fields.personal" :key="`personal-${index}`")
              field-value(
                ref="personalFields"
                :is-edit-mode="true"
                :field-name="field.name"
                :field-label="field.label"
                :field-type="field.type"
                v-model="params[field.name]"
                :prepend-icon="field.icon"
                :error-types='fieldValueErrorTypes'
                :error-messages="fieldValueErrorMessages"
                @field-status="fieldStatusChange"
                @input="resetEmailValidationErrors"
              )
            v-flex.mt-2(xs6)
              h3 Addresses
            v-flex.text-xs-right(xs6)
              v-btn.mx-0(
                data-cy="add-address"
                flat
                small
                @click="addNewAddress"
                color="primary"
              )
                v-icon add
                | Add new address
            v-flex(xs12 data-cy="addresses")
              address-type(
                v-for="(address, index) in params.addresses"
                :data-cy="`address-${index}`"
                :key="`addr-${index}`"
                :is-edit-mode="true" :types="addressTypes"
                :value="params.addresses[index]"
                @input="addAddress($event, index)"
                @delete="removeAddress(index)"
              )
            v-flex.mt-2(xs12)
              h3 Spouse Information
            v-flex(xs12 v-for="(field, index) in fields.spouse" :key="`spouse-${index}`")
              field-value(
                ref="spouseField"
                :field-type="field.type"
                :is-edit-mode="true"
                :field-name="field.name"
                v-model="params[field.name]"
                @field-status="fieldStatusChange"
                :field-label="field.label"
                :prepend-icon="field.icon"
                :error-types='fieldValueErrorTypes'
                :error-messages="fieldValueErrorMessages"
                @input="resetEmailValidationErrors"
              )
            v-flex.mt-2(xs12)
              h3 Additional Information
            v-flex(xs12 v-for="(field, index) in fields.organize" :key="`organize-${index}`")
              v-select(
                v-if="isDropDown(field.type)"
                :items="field.items"
                v-model="params[field.name]"
                :label="field.label"
                :prepend-icon="field.icon"
              )
              tags-picker(
                v-else-if="isAutocomplete(field.type)"
                v-model="params[field.name]"
                :disabled="false"
                :tags-list="field.items"
                @update:searchInput="findTags"
                @save:tag="createTag"
              )
              v-textarea(
                v-else
                :label="field.label"
                v-model="params[field.name]"
                :prepend-icon="field.icon"
              )
        v-card-actions
          v-flex(xs12)
            v-layout(row)
              v-flex(xs12)
                v-layout(row wrap justify-end)
                  v-spacer
                  v-btn(data-cy="cancel-contact" flat color="primary" @click="close") Cancel
                  v-btn.primary(data-cy="save-contact" :disabled="!areFieldsValid || !isRelationValid || !firstNameValid" @click="onSubmit") Create Contact
            v-layout(row v-if="showRelation")
              v-flex(xs12)
                v-layout(row wrap justify-end)
                  span.mt-2 This will add your new contact as the selected related type

    v-snackbar(:top="true" :value="errorState" color="error")
      div(v-for="error in errors") {{ error }}
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import TagsPicker from '@/Core/components/TagsPicker';
import TitleBar from '@/Core/components/TitleBar';
import FieldValue from '@/Contacts/components/profile/FieldValue';
import AddressType from '@/Contacts/components/profile/AddressType';
import contactCreateFields from '@/Contacts/config/contact-create-fields';
import {
  emailErrorMessages,
  primaryEmailLabel,
  secondaryEmailLabel,
  spouseEmailLabel,
  typeRelationLabel,
  relationErrorMessage,
  validateEmails,
  validateRelation,
} from '@/Contacts/utilities';
import { RELATION_TYPE_SPOUSE } from '@/Contacts/store/contact-relationships';
import {FIRST_NAME_MIN_LENGTH, FIRST_NAME_RULES, FIRST_NAME_PROHIBITED_WORDS} from '../config/contact-fields-rules'

export default {
  components: {
    FieldValue,
    AddressType,
    TitleBar,
    TagsPicker,
  },
  data() {
    return {
      errorState: false,
      errors: [],
      invalidFields: [],
      areFieldsValid: true,
      fieldValueErrorTypes: [],
      fieldValueErrorMessages: {},
      currentAddress: {},
      params: {
        addresses: [],
        tags: [],
        first_name: "",
      },
      relation: {
        label: typeRelationLabel,
        name: 'relation',
        icon: 'groups',
        type: 'select',
        items: [],
        hint: relationErrorMessage,
      },
      firstNameValid: true,
      firstNameRules: FIRST_NAME_RULES,
      firstNameMinLegth: FIRST_NAME_MIN_LENGTH,
      firstNameProhibitedWords: FIRST_NAME_PROHIBITED_WORDS,
    };
  },
  computed: {
    fields() {
      return contactCreateFields(
        this.sources,
        this.statuses,
        this.hotnesses,
        this.tags,
        this.teamMembers,
      );
    },
    isRelationValid() {
      return !this.showRelation || this.params.relation;
    },
    relationshipTypes() {
      const isSpouseAdded = this.relationshipsSpouse.length;
      if (isSpouseAdded) {
        return this.relationshipTypeItems.map(r => {
          r.disabled = r.text === RELATION_TYPE_SPOUSE;
          return r;
        });
      }
      return this.relationshipTypeItems;
    },
    ...mapState({
      hasError: state => state.contact.hasError,
      addressTypes: state => state.contact.addressTypes,
      statuses: state => state.contact.statuses,
      contactInProgress: state => state.contact.contactInProgress,
      contact: state => state.contact.contact,
      tags: state => state.contact.tags,
      hotnesses: state => state.contact.hotnesses,
      showCreateModal: state => state.contact.showCreateModal,
      showRelation: state => state.contact.showRelationField,
      contactId: state => state.contact.contactSelected,
    }),
    ...mapGetters({
      teamMembers: 'teamMembers/teamMembersInfo',
      sources: 'contact/sourcesWithDefaultItem',
    }),
    ...mapGetters({
      relationshipTypeItems: 'contactRelationships/relationshipTypeItems',
      relationshipsSpouse: 'contactRelationships/relationshipsSpouse'
    })
  },
  watch: {
    invalidFields(data) {
      this.areFieldsValid = !Boolean(data.length);
    },
    async showCreateModal(value) {
      if (value) {
        await this.findAddressTypes();
        await this.loadContactSources();
        await this.findTeamMembers();
        await this.loadContactHotnesses();
        await this.findTags();
        if(this.showRelation) {
          await this.fetchRelationshipTypes();
          this.relation.items = this.relationshipTypes;
        }
      }
    },
  },
  methods: {
      ...mapActions('contact', [
      'findAddressTypes',
      'loadContactSources',
      'createContact',
      'findTags',
      'createTag',
    ]),
    ...mapActions('contactsTable', [
      'resetContacts',
    ]),
    ...mapActions('contact', ['loadContactHotnesses', 'resetShowCreateModal', 'resetShowRelationField']),
    ...mapActions('teamMembers', ['findTeamMembers']),
    ...mapActions('contactRelationships', ['fetchRelationshipTypes', 'createContactRelationships', 'fetchContactRelationships']),
    fieldStatusChange(field) {
      const fieldIndex = this.invalidFields.indexOf(field.name);
      if (fieldIndex === -1 && !field.isValid) {
        this.invalidFields.push(field.name);
      }
      if (fieldIndex > -1 && field.isValid){
        this.invalidFields.splice(fieldIndex, 1);
      }
    },
    isAutocomplete(type) {
      return type === 'autocomplete';
    },
    isDropDown(type) {
      return type === 'select';
    },
    close() {
      this.resetEmailValidationErrors();
      this.resetShowCreateModal();
      this.resetShowRelationField();
      // goes back to the top of the form even when user scrolled down before closing the dialog
      let input = this.$refs.newContactForm.querySelector('input');
      input.focus();
      // we need to blur the input again, otherwise the form validation will run on re-open
      input.blur();
      this.params = Object.assign(
        {},
        {
          addresses: [],
          tags: [],
        },
      );
      this.$refs.firstNameForm.resetValidation();
    },

    // Reset error state for email validations
    resetEmailValidationErrors() {
      this.fieldValueErrorTypes = [];
      this.fieldValueErrorMessages = {};
    },

    async onSubmit() {
      if (!this.$refs.firstNameForm.validate()) return;
      this.errorState = false;
      this.errors = [];
      this.resetEmailValidationErrors();

      const primaryEmailField = this.$refs.personalFields.find(
        personalField => personalField.fieldLabel === primaryEmailLabel,
      );
      const secondaryEmailField = this.$refs.personalFields.find(
        personalField => personalField.fieldLabel === secondaryEmailLabel,
      );
      const spouseEmailField = this.$refs.spouseField.find(
        spouseField => spouseField.fieldLabel === spouseEmailLabel,
      );

      const { email: primaryEmail, secondary_email: secondaryEmail, spouse_email: spouseEmail, relation } = this.params;

      validateEmails(
        this,
        primaryEmail,
        secondaryEmail,
        spouseEmail,
        primaryEmailField,
        secondaryEmailField,
        spouseEmailField
      );

      if(this.showRelation) validateRelation(this, relation, this.$refs.relationField);

      if (this.fieldValueErrorTypes.length) return;

      try {
        const contactCreated = await this.createContact(this.params);
        if(this.showRelation) {
          const relationships = [{contact_id: contactCreated.id, contact_type_id: relation}]
          await this.createContactRelationships({contactId: this.contactId, relationships})
          await this.fetchContactRelationships(this.contactId);
        };
        this.resetContacts();
        if(this.showRelation) {
          this.close();
          return;
        }
        this.close();
        this.$router.push(`/contacts/${this.contact.id}`);
      } catch (error) {
        if(this.showRelation && error.message.includes("relationship")) {
          this.errorState = true;
          this.errors = [error.message];
        } else if (error.message.match(new RegExp(emailErrorMessages.duplicateAddress))) {
          this.fieldValueErrorTypes.push(primaryEmailLabel);
          this.fieldValueErrorMessages[primaryEmailLabel] = emailErrorMessages.duplicateAddress;
          primaryEmailField.focus();
        } else {
          this.errorState = true;
          this.errors = [error.message];
        }
      }

    },
    addNewAddress() {
      this.params.addresses.push({});
    },
    addAddress(newAddress, index) {
      this.currentAddress = newAddress;
      this.params.addresses[index] = this.currentAddress;
      this.currentAddress = {};
    },
    removeAddress(index) {
      this.params.addresses.splice(index, 1);
    },
  },
};
</script>
